import {
  calendarDaysFromLastTouchToToday,
  getLastTouchAt,
  getPersonaArchetypeName,
  getPersonaTableDepartment,
  resolvePersonaTouches,
} from '@/lib/engagementPersonaUtils';

const API_BASE_URL =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

export const ENGAGEMENT_SHORTLIST_VIEW_KEY = 'engagement_shortlist';

export const NUMERIC_OP_OPTIONS = [
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'eq', label: '=' },
  { value: 'gte', label: '≥' },
  { value: 'gt', label: '>' },
];

const OP_LABELS = Object.fromEntries(NUMERIC_OP_OPTIONS.map((o) => [o.value, o.label]));

function apiHeaders(tenantId, userId) {
  const headers = { 'Content-Type': 'application/json' };
  if (tenantId) headers['x-tenant-id'] = tenantId;
  if (userId) headers['x-user-id'] = userId;
  return headers;
}

export function emptyEngagementViewConfig() {
  return {
    archetypes: [],
    tiers: [],
    account_types: [],
    departments: [],
    persona_fit_score_min: '',
    role_title_q: '',
    product_affinity: [],
    touches: { op: 'lt', value: '' },
    last_touch_days: { op: 'gte', value: '' },
  };
}

export function normalizeViewConfigFromApi(raw) {
  const cfg = raw && typeof raw === 'object' ? raw : {};
  return {
    archetypes: Array.isArray(cfg.archetypes) ? cfg.archetypes.map(String) : [],
    tiers: Array.isArray(cfg.tiers) ? cfg.tiers.map(String) : [],
    account_types: Array.isArray(cfg.account_types) ? cfg.account_types.map(String) : [],
    departments: Array.isArray(cfg.departments) ? cfg.departments.map(String) : [],
    persona_fit_score_min:
      cfg.persona_fit_score_min != null && cfg.persona_fit_score_min !== ''
        ? Number(cfg.persona_fit_score_min)
        : '',
    role_title_q: cfg.role_title_q != null ? String(cfg.role_title_q) : '',
    product_affinity: Array.isArray(cfg.product_affinity)
      ? cfg.product_affinity.map(String)
      : [],
    touches: cfg.touches?.op
      ? { op: String(cfg.touches.op), value: cfg.touches.value ?? '' }
      : { op: 'lt', value: '' },
    last_touch_days: cfg.last_touch_days?.op
      ? { op: String(cfg.last_touch_days.op), value: cfg.last_touch_days.value ?? '' }
      : { op: 'gte', value: '' },
  };
}

export function serializeViewConfigForApi(form) {
  const out = {};
  if (form.archetypes?.length) out.archetypes = form.archetypes;
  if (form.tiers?.length) out.tiers = form.tiers;
  if (form.account_types?.length) out.account_types = form.account_types;
  if (form.departments?.length) out.departments = form.departments;
  if (form.persona_fit_score_min !== '' && form.persona_fit_score_min != null) {
    const n = Number(form.persona_fit_score_min);
    if (Number.isFinite(n)) out.persona_fit_score_min = n;
  }
  const roleQ = String(form.role_title_q || '').trim();
  if (roleQ) out.role_title_q = roleQ;
  if (form.product_affinity?.length) out.product_affinity = form.product_affinity;
  const touchesVal = form.touches?.value;
  if (touchesVal !== '' && touchesVal != null && form.touches?.op) {
    const n = Number(touchesVal);
    if (Number.isFinite(n)) out.touches = { op: form.touches.op, value: n };
  }
  const lastTouchVal = form.last_touch_days?.value;
  if (lastTouchVal !== '' && lastTouchVal != null && form.last_touch_days?.op) {
    const n = Number(lastTouchVal);
    if (Number.isFinite(n)) out.last_touch_days = { op: form.last_touch_days.op, value: n };
  }
  return out;
}

function compareNumericFilter(actual, filter) {
  if (!filter?.op) return true;
  const expected = Number(filter.value);
  if (!Number.isFinite(expected)) return true;
  const actualNum = Number(actual);
  if (!Number.isFinite(actualNum)) return false;
  switch (filter.op) {
    case 'lt':
      return actualNum < expected;
    case 'lte':
      return actualNum <= expected;
    case 'eq':
      return actualNum === expected;
    case 'gte':
      return actualNum >= expected;
    case 'gt':
      return actualNum > expected;
    default:
      return true;
  }
}

export function applyEngagementViewConfig(rows, viewConfig, helpers) {
  const cfg = viewConfig && typeof viewConfig === 'object' ? viewConfig : {};
  return rows.filter((p) => {
    if (cfg.archetypes?.length) {
      const arch = getPersonaArchetypeName(p);
      if (!arch || !cfg.archetypes.includes(arch)) return false;
    }
    if (cfg.tiers?.length) {
      const tier = helpers.getRowAccountTier(p);
      if (!tier || !cfg.tiers.includes(tier)) return false;
    }
    if (cfg.account_types?.length) {
      const accountType = helpers.getRowAccountSector?.(p);
      if (!accountType || !cfg.account_types.includes(accountType)) return false;
    }
    if (cfg.departments?.length) {
      const dept = getPersonaTableDepartment(p);
      if (!dept || !cfg.departments.includes(dept)) return false;
    }
    if (cfg.persona_fit_score_min != null && cfg.persona_fit_score_min !== '') {
      const score = Number(p.persona_fit_score);
      if (!Number.isFinite(score) || score < Number(cfg.persona_fit_score_min)) return false;
    }
    if (cfg.role_title_q) {
      const role = String(p.role_title || p.title || p.job_title || '').toLowerCase();
      if (!role.includes(String(cfg.role_title_q).trim().toLowerCase())) return false;
    }
    if (cfg.product_affinity?.length) {
      const pa = String(p.product_affinity ?? '').trim();
      if (!pa || !cfg.product_affinity.includes(pa)) return false;
    }
    if (cfg.touches?.op && cfg.touches.value != null && cfg.touches.value !== '') {
      if (!compareNumericFilter(resolvePersonaTouches(p), cfg.touches)) return false;
    }
    if (cfg.last_touch_days?.op && cfg.last_touch_days.value != null && cfg.last_touch_days.value !== '') {
      const days = calendarDaysFromLastTouchToToday(getLastTouchAt(p));
      if (days === null) return false;
      if (!compareNumericFilter(days, cfg.last_touch_days)) return false;
    }
    return true;
  });
}

export function buildViewConfigSummaryChips(viewConfig) {
  const cfg = viewConfig && typeof viewConfig === 'object' ? viewConfig : {};
  const chips = [];
  if (cfg.archetypes?.length) {
    chips.push(`Archetype: ${cfg.archetypes.join(', ')}`);
  }
  if (cfg.tiers?.length) {
    chips.push(`Tier: ${cfg.tiers.join(', ')}`);
  }
  if (cfg.account_types?.length) {
    chips.push(`Account type: ${cfg.account_types.slice(0, 2).join(', ')}${cfg.account_types.length > 2 ? '…' : ''}`);
  }
  if (cfg.departments?.length) {
    chips.push(`Department: ${cfg.departments.slice(0, 2).join(', ')}${cfg.departments.length > 2 ? '…' : ''}`);
  }
  if (cfg.persona_fit_score_min != null && cfg.persona_fit_score_min !== '') {
    chips.push(`Persona fit ≥ ${cfg.persona_fit_score_min}`);
  }
  if (cfg.role_title_q) {
    chips.push(`Role: "${String(cfg.role_title_q).trim()}"`);
  }
  if (cfg.product_affinity?.length) {
    chips.push(`Product affinity: ${cfg.product_affinity.join(', ')}`);
  }
  if (cfg.touches?.op && cfg.touches.value != null && cfg.touches.value !== '') {
    chips.push(`Touches ${OP_LABELS[cfg.touches.op] || cfg.touches.op} ${cfg.touches.value}`);
  }
  if (cfg.last_touch_days?.op && cfg.last_touch_days.value != null && cfg.last_touch_days.value !== '') {
    const op = OP_LABELS[cfg.last_touch_days.op] || cfg.last_touch_days.op;
    chips.push(`Last touch ${op} ${cfg.last_touch_days.value}d`);
  }
  return chips;
}

export async function fetchEngagementSavedViews(tenantId, userId) {
  const params = new URLSearchParams({
    view_key: ENGAGEMENT_SHORTLIST_VIEW_KEY,
    tenant_id: tenantId,
  });
  if (userId) params.set('user_id', userId);
  const res = await fetch(`${API_BASE_URL}/api/saved-views?${params.toString()}`, {
    credentials: 'include',
    headers: apiHeaders(tenantId, userId),
  });
  if (!res.ok) throw new Error('Failed to load saved views');
  const json = await res.json();
  const views = Array.isArray(json?.views) ? json.views : [];
  return views.sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
}

export async function createEngagementSavedView(tenantId, userId, { name, view_config, sort_order }) {
  const res = await fetch(`${API_BASE_URL}/api/saved-views`, {
    method: 'POST',
    credentials: 'include',
    headers: apiHeaders(tenantId, userId),
    body: JSON.stringify({
      view_key: ENGAGEMENT_SHORTLIST_VIEW_KEY,
      name,
      view_config,
      sort_order,
      tenant_id: tenantId,
      user_id: userId,
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 409) {
    const err = new Error(json?.error || json?.message || 'A view with this name already exists');
    err.status = 409;
    throw err;
  }
  if (!res.ok) throw new Error(json?.error || json?.message || 'Failed to create view');
  return json.view;
}

export async function updateEngagementSavedView(tenantId, userId, id, patch) {
  const res = await fetch(`${API_BASE_URL}/api/saved-views/${encodeURIComponent(id)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: apiHeaders(tenantId, userId),
    body: JSON.stringify({ ...patch, tenant_id: tenantId, user_id: userId }),
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 409) {
    const err = new Error(json?.error || json?.message || 'A view with this name already exists');
    err.status = 409;
    throw err;
  }
  if (!res.ok) throw new Error(json?.error || json?.message || 'Failed to update view');
  return json.view;
}

export async function deleteEngagementSavedView(tenantId, userId, id) {
  const res = await fetch(`${API_BASE_URL}/api/saved-views/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: apiHeaders(tenantId, userId),
    body: JSON.stringify({ tenant_id: tenantId, user_id: userId }),
  });
  if (!res.ok) throw new Error('Failed to delete view');
  return true;
}

export function collectProductAffinityOptions(rows) {
  const set = new Set();
  for (const p of rows || []) {
    const v = String(p?.product_affinity ?? '').trim();
    if (v) set.add(v);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}
